import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppState } from "../../context/AppStateContext";
import { useCustomerSearch, useDiscountRules, useStampsNeeded } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { localName } from "../../lib/format";
import { ruleValueLabel } from "../cashier/CouponPicker";
import { initials } from "../../lib/format";
import { Avatar, Empty, SectionHeader } from "../ui/Badges";
import { StampCard } from "./StampCard";

export function LoyaltyPanel() {
  const { t } = useTranslation();
  const { startCheckIn } = useAppState();
  const [q, setQ] = useState("");
  const { data, isFetching } = useCustomerSearch(q);
  const need = useStampsNeeded();
  const { locale } = useLocale();
  const coupons = useDiscountRules();

  return (
    <div className="space-y-5">
      {!!coupons.data?.length && (
        <section className="card space-y-2 p-4 sm:p-5" aria-label={t("coupon.title")}>
          <SectionHeader icon="🎟️" title={t("coupon.title")} subtitle={t("coupon.howToUse")} />
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {coupons.data.map((r) => (
              <div key={r.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-black text-cosmic-navy">{localName(r, locale)}</span>
                  <span className="num rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-black text-amber-900">{ruleValueLabel(r, t("currency"))}</span>
                </div>
                <p className="mt-1 text-[11px] text-gray-600">{t(`coupon.cond.${r.condition}`)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="card space-y-3 p-4 sm:p-5">
        <SectionHeader icon="⭐" title={t("loyalty.title")} subtitle={t("loyalty.subtitle")} />
        <div>
          <label className="label" htmlFor="loy-q">{t("loyalty.search")}</label>
          <input id="loy-q" className="field" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("loyalty.searchHint")} autoFocus />
        </div>
        {need === 0 ? <Empty>{t("loyalty.off")}</Empty> : q.trim().length < 3 ? <Empty>{t("loyalty.typeToSearch")}</Empty> : isFetching && !data ? <Empty>{t("common.loading")}</Empty> : !data?.length ? <Empty>{t("loyalty.noResults")}</Empty> : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {data.map((g) => (
              <article key={g.id} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3.5">
                <div className="mb-2 flex items-center gap-2.5">
                  <Avatar text={initials(g.fullName)} />
                  <div>
                    <div className="text-sm font-extrabold text-cosmic-navy">{g.fullName}</div>
                    <div className="num font-mono text-[11px] text-gray-500">{g.phone}</div>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {g.kids.length === 0 && <p className="text-xs text-gray-400">{t("loyalty.noKids")}</p>}
                  {g.kids.map((k) => (
                    <div key={k.id} className="rounded-xl border border-gray-200 bg-white p-2.5">
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-xs font-black">{k.name}</span>
                        <span className="num text-[11px] font-bold text-gray-500">{t("loyalty.stampsOf", { count: k.stamps, total: need })}</span>
                      </div>
                      <StampCard stamps={k.stamps} total={need} />
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {k.stamps >= need ? <span className="text-[11px] font-black text-amber-700">🎁 {t("loyalty.freeAvailable")}</span> : <span className="text-[11px] text-gray-400">{t("loyalty.toGo", { count: need - k.stamps })}</span>}
                        <button type="button" className={k.stamps >= need ? "btn-primary" : "btn-ghost"} onClick={() => startCheckIn({ guardian: { id: g.id, fullName: g.fullName, phone: g.phone }, kid: { id: k.id, name: k.name, stamps: k.stamps }, redeem: k.stamps >= need })}>
                          {k.stamps >= need ? t("loyalty.redeem") : t("loyalty.checkIn")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
